import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  BookOpen, Heart, Shield, Stethoscope, Baby, Sparkles, MessageCircleHeart,
  CheckCircle2, XCircle, AlertCircle, Pill, Activity, HandHeart,
} from "lucide-react";

const SECTIONS = [
  { id: "basics", icon: BookOpen },
  { id: "spread", icon: Activity },
  { id: "shedding", icon: Sparkles },
  { id: "reduce", icon: Shield },
  { id: "symptoms", icon: AlertCircle },
  { id: "testing", icon: Stethoscope },
  { id: "pregnancy", icon: Baby },
  { id: "myths", icon: CheckCircle2 },
  { id: "newly", icon: Heart },
  { id: "disclose", icon: MessageCircleHeart },
  { id: "disclaimer", icon: HandHeart },
];

function SectionHeader({ icon: Icon, kickerKey, titleKey, id }) {
  const { t } = useTranslation();
  return (
    <div id={id} className="mb-4 scroll-mt-24">
      <div className="flex items-center gap-2 text-[#B45309] text-xs uppercase tracking-widest font-semibold mb-2">
        <Icon className="w-4 h-4" /> {t(kickerKey)}
      </div>
      <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C2623] dark:text-[#FAF5F0] tracking-tight">
        {t(titleKey)}
      </h2>
    </div>
  );
}

function InfoCard({ children, className = "", ...props }) {
  return (
    <Card className={`p-6 sm:p-7 rounded-3xl border-[#E8DEC8] bg-white dark:bg-[#221E1C] dark:border-[#423B36] ${className}`} {...props}>
      {children}
    </Card>
  );
}

function Bullet({ labelKey, bodyKey }) {
  const { t } = useTranslation();
  return (
    <li className="flex gap-3">
      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#D95D39] shrink-0" />
      <div>
        {labelKey && <span className="font-semibold text-[#2C2623] dark:text-[#FAF5F0]">{t(labelKey)} </span>}
        <span className="text-[#4A413B] dark:text-[#B5AAA1]">{t(bodyKey)}</span>
      </div>
    </li>
  );
}

const MYTH_IDS = [1, 2, 3, 4, 5];

export default function Awareness() {
  const { t } = useTranslation();
  const [openMyth, setOpenMyth] = useState(null);

  return (
    <div className="min-h-screen bg-[#FFFDF9] dark:bg-[#1A1615]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="awareness-page">
        <div className="text-center mb-10 sm:mb-14" data-testid="awareness-hero">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFF0EC] text-[#C24C29] text-sm font-medium mb-4 dark:bg-[#3A211B]">
            <BookOpen className="w-4 h-4" /> {t("awareness.hero_kicker")}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C2623] dark:text-[#FAF5F0] tracking-tight">
            {t("awareness.hero_title")}
          </h1>
          <p className="text-base sm:text-lg text-[#6E655F] dark:text-[#B5AAA1] mt-4 max-w-2xl mx-auto leading-relaxed">
            {t("awareness.hero_intro")}
          </p>
        </div>

        <div className="grid lg:grid-cols-[220px_1fr] gap-8 lg:gap-12">
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="awareness-toc">
            <div className="text-xs uppercase tracking-widest text-[#6E655F] dark:text-[#B5AAA1] font-semibold mb-3">{t("awareness.toc_label")}</div>
            <ul className="space-y-1">
              {SECTIONS.map(({ id, icon: Icon }) => (
                <li key={id}>
                  <a href={`#${id}`}
                    data-testid={`awareness-toc-${id}`}
                    className="flex items-center gap-2 text-sm text-[#4A413B] dark:text-[#B5AAA1] hover:text-[#C24C29] dark:hover:text-[#E07A5F] py-1.5 px-2 rounded-lg hover:bg-[#F9F5EE] dark:hover:bg-[#2A2422] transition-colors">
                    <Icon className="w-3.5 h-3.5" /> {t(`awareness.toc.${id}`)}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <div className="space-y-10 sm:space-y-12">
            <section>
              <SectionHeader id="basics" icon={BookOpen}
                kickerKey="awareness.sec_basics_kicker" titleKey="awareness.sec_basics_title" />
              <div className="grid sm:grid-cols-2 gap-4">
                <InfoCard data-testid="card-hsv-1">
                  <Badge className="mb-3 bg-[#DDEDE4] text-[#2A524A] hover:bg-[#DDEDE4]">HSV-1</Badge>
                  <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t("awareness.sec_basics_hsv1_body")}</p>
                </InfoCard>
                <InfoCard data-testid="card-hsv-2">
                  <Badge className="mb-3 bg-[#F9E1DA] text-[#C24C29] hover:bg-[#F9E1DA]">HSV-2</Badge>
                  <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t("awareness.sec_basics_hsv2_body")}</p>
                </InfoCard>
              </div>
              <p className="text-sm text-[#6E655F] dark:text-[#B5AAA1] mt-4 italic">{t("awareness.sec_basics_note")}</p>
            </section>

            <section>
              <SectionHeader id="spread" icon={Activity}
                kickerKey="awareness.sec_spread_kicker" titleKey="awareness.sec_spread_title" />
              <InfoCard>
                <ul className="space-y-3">
                  <Bullet labelKey="awareness.sec_spread_b1_label" bodyKey="awareness.sec_spread_b1_body" />
                  <Bullet labelKey="awareness.sec_spread_b2_label" bodyKey="awareness.sec_spread_b2_body" />
                  <Bullet labelKey="awareness.sec_spread_b3_label" bodyKey="awareness.sec_spread_b3_body" />
                </ul>
              </InfoCard>
            </section>

            <section>
              <SectionHeader id="shedding" icon={Sparkles}
                kickerKey="awareness.sec_shedding_kicker" titleKey="awareness.sec_shedding_title" />
              <InfoCard data-testid="card-shedding">
                <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">
                  {t("awareness.sec_shedding_p1_pre")} <b className="text-[#2C2623] dark:text-[#FAF5F0]">{t("awareness.sec_shedding_p1_bold")}</b>{t("awareness.sec_shedding_p1_post")}
                </p>
                <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed mt-3">{t("awareness.sec_shedding_p2")}</p>
              </InfoCard>
            </section>

            <section>
              <SectionHeader id="reduce" icon={Shield}
                kickerKey="awareness.sec_reduce_kicker" titleKey="awareness.sec_reduce_title" />
              <div className="grid sm:grid-cols-3 gap-4">
                <InfoCard data-testid="card-avoid-outbreak">
                  <AlertCircle className="w-6 h-6 text-[#D95D39] mb-3" />
                  <h3 className="font-serif text-lg font-semibold text-[#2C2623] dark:text-[#FAF5F0] mb-2">{t("awareness.sec_reduce_c1_title")}</h3>
                  <p className="text-sm text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t("awareness.sec_reduce_c1_body")}</p>
                </InfoCard>
                <InfoCard data-testid="card-barriers">
                  <Shield className="w-6 h-6 text-[#D95D39] mb-3" />
                  <h3 className="font-serif text-lg font-semibold text-[#2C2623] dark:text-[#FAF5F0] mb-2">{t("awareness.sec_reduce_c2_title")}</h3>
                  <p className="text-sm text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t("awareness.sec_reduce_c2_body")}</p>
                </InfoCard>
                <InfoCard data-testid="card-antivirals">
                  <Pill className="w-6 h-6 text-[#D95D39] mb-3" />
                  <h3 className="font-serif text-lg font-semibold text-[#2C2623] dark:text-[#FAF5F0] mb-2">{t("awareness.sec_reduce_c3_title")}</h3>
                  <p className="text-sm text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t("awareness.sec_reduce_c3_body")}</p>
                </InfoCard>
              </div>
              <p className="text-sm text-[#6E655F] dark:text-[#B5AAA1] mt-4 italic">{t("awareness.sec_reduce_note")}</p>
            </section>

            <section>
              <SectionHeader id="symptoms" icon={AlertCircle}
                kickerKey="awareness.sec_symptoms_kicker" titleKey="awareness.sec_symptoms_title" />
              <InfoCard>
                <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed mb-4">{t("awareness.sec_symptoms_intro")}</p>
                <ul className="space-y-3">
                  <Bullet labelKey="awareness.sec_symptoms_b1_label" bodyKey="awareness.sec_symptoms_b1_body" />
                  <Bullet labelKey="awareness.sec_symptoms_b2_label" bodyKey="awareness.sec_symptoms_b2_body" />
                  <Bullet labelKey="awareness.sec_symptoms_b3_label" bodyKey="awareness.sec_symptoms_b3_body" />
                  <Bullet labelKey="awareness.sec_symptoms_b4_label" bodyKey="awareness.sec_symptoms_b4_body" />
                </ul>
                <p className="text-sm text-[#6E655F] dark:text-[#B5AAA1] mt-4">{t("awareness.sec_symptoms_note")}</p>
              </InfoCard>
            </section>

            <section>
              <SectionHeader id="testing" icon={Stethoscope}
                kickerKey="awareness.sec_testing_kicker" titleKey="awareness.sec_testing_title" />
              <InfoCard>
                <ul className="space-y-3">
                  <Bullet labelKey="awareness.sec_testing_b1_label" bodyKey="awareness.sec_testing_b1_body" />
                  <Bullet labelKey="awareness.sec_testing_b2_label" bodyKey="awareness.sec_testing_b2_body" />
                  <Bullet labelKey="awareness.sec_testing_b3_label" bodyKey="awareness.sec_testing_b3_body" />
                  <Bullet labelKey="awareness.sec_testing_b4_label" bodyKey="awareness.sec_testing_b4_body" />
                </ul>
              </InfoCard>
            </section>

            <section>
              <SectionHeader id="pregnancy" icon={Baby}
                kickerKey="awareness.sec_pregnancy_kicker" titleKey="awareness.sec_pregnancy_title" />
              <InfoCard data-testid="card-pregnancy">
                <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">
                  {t("awareness.sec_pregnancy_p1_pre")} <b className="text-[#2C2623] dark:text-[#FAF5F0]">{t("awareness.sec_pregnancy_p1_bold")}</b>{t("awareness.sec_pregnancy_p1_post")}
                </p>
                <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed mt-3">
                  {t("awareness.sec_pregnancy_p2_pre")} <b className="text-[#2C2623] dark:text-[#FAF5F0]">{t("awareness.sec_pregnancy_p2_bold")}</b>{t("awareness.sec_pregnancy_p2_post")}
                </p>
              </InfoCard>
            </section>

            <section>
              <SectionHeader id="myths" icon={CheckCircle2}
                kickerKey="awareness.sec_myths_kicker" titleKey="awareness.sec_myths_title" />
              <div className="space-y-3" data-testid="myths-list">
                {MYTH_IDS.map((i, idx) => {
                  const isOpen = openMyth === idx;
                  return (
                    <InfoCard key={i} className="!p-0 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenMyth(isOpen ? null : idx)}
                        data-testid={`myth-toggle-${idx}`}
                        className="w-full text-left p-5 sm:p-6 flex items-start gap-3 hover:bg-[#FFFBF3] dark:hover:bg-[#2A2422] transition-colors">
                        <XCircle className="w-5 h-5 text-[#D95D39] shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-xs uppercase tracking-widest text-[#B45309] font-semibold mb-1">{t("awareness.sec_myths_myth_label")}</div>
                          <div className="text-[#2C2623] dark:text-[#FAF5F0] font-medium">{t(`awareness.myth_${i}_myth`)}</div>
                        </div>
                        <span className="text-[#6E655F] text-xl leading-none">{isOpen ? "−" : "+"}</span>
                      </button>
                      {isOpen && (
                        <div className="px-5 sm:px-6 pb-5 sm:pb-6 flex items-start gap-3 border-t border-[#F0E6D2] dark:border-[#3A322F] pt-4" data-testid={`myth-fact-${idx}`}>
                          <CheckCircle2 className="w-5 h-5 text-[#2A524A] shrink-0 mt-0.5" />
                          <div>
                            <div className="text-xs uppercase tracking-widest text-[#2A524A] font-semibold mb-1">{t("awareness.sec_myths_fact_label")}</div>
                            <div className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t(`awareness.myth_${i}_fact`)}</div>
                          </div>
                        </div>
                      )}
                    </InfoCard>
                  );
                })}
              </div>
            </section>

            <section>
              <SectionHeader id="newly" icon={Heart}
                kickerKey="awareness.sec_newly_kicker" titleKey="awareness.sec_newly_title" />
              <InfoCard className="border-l-4 !border-l-[#D95D39]" data-testid="card-newly-diagnosed">
                <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t("awareness.sec_newly_intro")}</p>
                <ul className="space-y-3 mt-4">
                  <Bullet labelKey="awareness.sec_newly_b1_label" bodyKey="awareness.sec_newly_b1_body" />
                  <Bullet labelKey="awareness.sec_newly_b2_label" bodyKey="awareness.sec_newly_b2_body" />
                  <Bullet labelKey="awareness.sec_newly_b3_label" bodyKey="awareness.sec_newly_b3_body" />
                  <Bullet labelKey="awareness.sec_newly_b4_label" bodyKey="awareness.sec_newly_b4_body" />
                  <Bullet labelKey="awareness.sec_newly_b5_label" bodyKey="awareness.sec_newly_b5_body" />
                </ul>
              </InfoCard>
            </section>

            <section>
              <SectionHeader id="disclose" icon={MessageCircleHeart}
                kickerKey="awareness.sec_disclose_kicker" titleKey="awareness.sec_disclose_title" />
              <InfoCard data-testid="card-disclose">
                <p className="text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t("awareness.sec_disclose_intro")}</p>
                <ul className="space-y-3 mt-4">
                  <Bullet labelKey="awareness.sec_disclose_b1_label" bodyKey="awareness.sec_disclose_b1_body" />
                  <Bullet labelKey="awareness.sec_disclose_b2_label" bodyKey="awareness.sec_disclose_b2_body" />
                  <Bullet labelKey="awareness.sec_disclose_b3_label" bodyKey="awareness.sec_disclose_b3_body" />
                  <Bullet labelKey="awareness.sec_disclose_b4_label" bodyKey="awareness.sec_disclose_b4_body" />
                  <Bullet labelKey="awareness.sec_disclose_b5_label" bodyKey="awareness.sec_disclose_b5_body" />
                </ul>
                <div className="mt-6 p-4 rounded-2xl bg-[#FFF0EC] border border-[#F4C9BE] dark:bg-[#3A211B] dark:border-[#5A342A]">
                  <div className="text-xs uppercase tracking-widest text-[#C24C29] font-semibold mb-2">{t("awareness.sec_disclose_script_label")}</div>
                  <p className="text-[#4A413B] dark:text-[#EBD9CE] leading-relaxed italic">{t("awareness.sec_disclose_script_body")}</p>
                </div>
              </InfoCard>
            </section>

            <section>
              <SectionHeader id="disclaimer" icon={HandHeart}
                kickerKey="awareness.sec_disclaimer_kicker" titleKey="awareness.sec_disclaimer_title" />
              <InfoCard className="bg-[#FBF6EB] dark:bg-[#2A2521]" data-testid="card-disclaimer">
                <p className="text-sm text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">{t("awareness.sec_disclaimer_body")}</p>
              </InfoCard>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
